import { useEffect, useState } from "react";
import { Apple, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Reveal } from "./Reveal";
import { LogoMark } from "./Logo";

const ROLES = [["everyday", "Everyday tester"], ["creator", "Content creator"], ["performer", "Adult performer"], ["worker", "Sex worker"]];

export const Waitlist = () => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("everyday");
  const [state, setState] = useState("idle");

  useEffect(() => {
    const onRole = (e) => setRole(e.detail);
    window.addEventListener("sr:waitlist-role", onRole);
    return () => window.removeEventListener("sr:waitlist-role", onRole);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      const { data } = await http.post("/waitlist", { email, role });
      setState("done");
      toast.success(data?.already ? "You're already on the list" : "You're on the waitlist!");
    } catch {
      setState("idle");
      toast.error("Please enter a valid email and try again");
    }
  };

  return (
    <section id="waitlist" className="relative px-5 sm:px-8 pb-24 lg:pb-36">
      <Reveal>
        <div className="relative max-w-7xl mx-auto rounded-[40px] overflow-hidden bg-[#FF2A8D] px-6 sm:px-12 lg:px-20 py-16 lg:py-24">
          <div className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full bg-[#125AA0] blur-[100px] opacity-80" />
          <LogoMark className="absolute right-8 top-8 w-24 h-24 rotate-6 shadow-2xl hidden sm:block" />
          <div className="relative max-w-2xl">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-white/80 flex items-center gap-2"><Apple size={14} /> iPhone app · coming soon</p>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-[0.95] mt-5">Be first in line.</h2>
            <p className="mt-5 text-white/90 text-base sm:text-lg">The Sweet Results app is on its way to the App Store. Join the waitlist and we'll tell you the moment it lands.</p>

            {state === "done" ? (
              <div data-testid="waitlist-success-msg" className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#070A14] px-6 py-4 font-semibold">
                <Check className="text-[#10B981]" /> You're on the list — see you at launch.
              </div>
            ) : (
              <form onSubmit={submit} className="mt-10">
                <div className="flex flex-wrap gap-2 mb-4" role="radiogroup" aria-label="I am a">
                  {ROLES.map(([v, l]) => (
                    <button type="button" key={v} data-testid={`waitlist-role-${v}`} onClick={() => setRole(v)} aria-pressed={role === v}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${role === v ? "bg-[#070A14] text-white" : "bg-white/20 hover:bg-white/30"}`}>{l}</button>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input data-testid="waitlist-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
                    className="flex-1 rounded-full bg-white text-[#070A14] placeholder:text-slate-500 px-6 py-4 focus:outline-none focus:ring-4 focus:ring-[#070A14]/30" />
                  <button data-testid="waitlist-submit-btn" disabled={state === "loading"}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#070A14] hover:bg-[#141A33] px-8 py-4 font-semibold transition-colors disabled:opacity-60">
                    {state === "loading" && <Loader2 className="animate-spin" size={18} />} Join waitlist
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </Reveal>
    </section>
  );
};
